import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  FileText,
  FolderOpen
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminCategory {
  id: number
  slug: string
  name: string
  description: string | null
  position: number
  community_name: string | null
  parent_id: number | null
  parent_name: string | null
  children_count: number
  posts_count: number
  articles_count: number
  created_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  community_id: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface IndexProps {
  categories: AdminCategory[]
  pagination: Pagination
  filters: Filters
  communities: Community[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export default function Index({ categories, pagination, filters, communities }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/categories', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/categories', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((category: AdminCategory) => {
    if (category.posts_count > 0 || category.articles_count > 0) {
      alert(`Impossible de supprimer la catégorie "${category.name}" car elle contient des publications ou articles.`)
      return
    }
    if (confirm(`Voulez-vous vraiment supprimer la catégorie "${category.name}" ?`)) {
      router.delete(`/admin/categories/${category.slug}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Catégories"
      breadcrumbs={[{ label: 'Catégories' }]}
    >
      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par nom ou description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Link href="/admin/categories/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Nouvelle catégorie
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautés</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          {(filters.search || filters.community_id) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  community_id: null
                })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} catégorie{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Categories Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('position')}
                  >
                    #
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('name')}
                  >
                    Catégorie
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Description
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Communauté
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('posts_count')}
                  >
                    <FileText size={14} />
                    Contenus
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Créé le
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <FolderOpen size={32} className="text-gray-300" />
                      Aucune catégorie trouvée
                    </div>
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-text-gray">
                        {category.position}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className={category.parent_id ? 'pl-4 border-l-2 border-gray-200' : ''}>
                        <div className="flex items-center gap-2">
                          {category.parent_name && (
                            <span className="text-xs text-text-gray bg-gray-100 px-2 py-0.5 rounded">
                              {category.parent_name}
                            </span>
                          )}
                          <p className="font-medium text-text-dark">{category.name}</p>
                          {category.children_count > 0 && (
                            <span className="text-xs text-tessan-primary bg-tessan-cream px-2 py-0.5 rounded">
                              {category.children_count} sous-cat.
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-gray">{category.slug}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {category.description ? (
                        <p className="text-sm text-text-gray line-clamp-2 max-w-xs">
                          {category.description}
                        </p>
                      ) : (
                        <span className="text-text-gray">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-dark">
                        {category.community_name || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-sm">
                          <FileText size={14} className="text-text-gray" />
                          <span className="font-medium text-text-dark">{category.posts_count}</span>
                          <span className="text-text-gray">posts</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm">
                          <FileText size={14} className="text-text-gray" />
                          <span className="font-medium text-text-dark">{category.articles_count}</span>
                          <span className="text-text-gray">articles</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {formatDate(category.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/admin/categories/${category.slug}`}>
                          <Button variant="secondary" className="p-2">
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Link href={`/admin/categories/${category.slug}/edit`}>
                          <Button variant="secondary" className="p-2">
                            <Edit size={16} />
                          </Button>
                        </Link>
                        <Button
                          variant="secondary"
                          className={`p-2 ${
                            category.posts_count > 0 || category.articles_count > 0
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          onClick={() => handleDelete(category)}
                          disabled={category.posts_count > 0 || category.articles_count > 0}
                          title={
                            category.posts_count > 0 || category.articles_count > 0
                              ? 'Impossible de supprimer : contient des contenus'
                              : 'Supprimer'
                          }
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Précédent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
